import React, { useState, useEffect, createContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SeoHeader from './SeoHeader';
import SeoFooter from './SeoFooter';

export const SeoEnquiryContext = createContext({
  openEnquiryModal: () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-seo-enquiry-modal'));
    }
  },
});

export const useSeoEnquiry = () => ({
  openEnquiryModal: () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-seo-enquiry-modal'));
    }
  },
});

const SeoLayout = ({ children }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    to: "leads@grank.co.in",
    subject: "Enquiry For G-Rank",
    name: "",
    phone: "",
    company: "",
    business: "none",
    city: "",
    email: "",
    message: "",
    policyConsent: true,
    cc: "suresh@grank.co.in, singam@grank.co.in",
    bcc: "server@grank.co.in",
    url: typeof window !== "undefined" ? window.location.href : "",
  });

  const siteKey = "6LcuJ9QmAAAAAGeBZPkD4Am3FwfPo7fJoowZQpvI";

  useEffect(() => {
    window.scrollTo(0, 0);
    setFormData((prev) => ({
      ...prev,
      url: window.location.href,
    }));

    const handleOpenModal = () => setOpenDialog(true);
    window.addEventListener('open-seo-enquiry-modal', handleOpenModal);
    return () => window.removeEventListener('open-seo-enquiry-modal', handleOpenModal);
  }, [location]);

  const handleOpen = () => setOpenDialog(true);
  const handleClose = () => setOpenDialog(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(siteKey, { action: "submit" })
            .then(async (token) => {
              const submissionData = {
                ...formData,
                url: window.location.href,
                token,
              };

              try {
                const response = await fetch(
                  "https://server.grank.co.in/send-email",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(submissionData),
                  }
                );

                if (response.ok) {
                  setOpenDialog(false);
                  navigate(`/seo-thankyou?email=${encodeURIComponent(formData.email)}`);
                } else {
                  setOpenDialog(false);
                  navigate(`/seo-thankyou?email=${encodeURIComponent(formData.email)}`);
                }
              } catch (err) {
                console.error("Error sending form:", err);
                setOpenDialog(false);
                navigate(`/seo-thankyou?email=${encodeURIComponent(formData.email)}`);
              } finally {
                setLoading(false);
              }
            })
            .catch(() => {
              setOpenDialog(false);
              navigate(`/seo-thankyou?email=${encodeURIComponent(formData.email)}`);
              setLoading(false);
            });
        });
      } else {
        await fetch("https://server.grank.co.in/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, url: window.location.href }),
        });
        setOpenDialog(false);
        navigate(`/seo-thankyou?email=${encodeURIComponent(formData.email)}`);
        setLoading(false);
      }
    } catch (error) {
      console.error("Submission failed:", error);
      setOpenDialog(false);
      navigate(`/seo-thankyou?email=${encodeURIComponent(formData.email)}`);
      setLoading(false);
    }
  };

  return (
    <SeoEnquiryContext.Provider value={{ openEnquiryModal: handleOpen }}>
      <div className="seo-layout">
        <Helmet>
          <link
            rel="stylesheet"
            href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css"
          />
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap"
            rel="stylesheet"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap"
            rel="stylesheet"
          />
          <link href="/seo-static/css/main.0525d737.css" rel="stylesheet" />
          <link
            rel="stylesheet"
            type="text/css"
            href="/seo-static/css/style-css/style.css"
          />
          <link
            rel="stylesheet"
            type="text/css"
            href="/seo-static/css/style-css/responsive-style.css"
          />
          <link
            rel="stylesheet"
            type="text/css"
            href="/seo-static/css/style-css/pagecss.css"
          />
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css"
          />
        </Helmet>

        <SeoHeader />

        <main>{children}</main>

        <SeoFooter />

        {/* Floating Vertical Enquire Now Button matching BlankLayout */}
        <ul className="enquire-button" onClick={handleOpen} title="Click to enquire" style={{ cursor: "pointer" }}>
          <li>
            <span>Enquire Now</span>
          </li>
        </ul>

        {/* Exact Enquire Now Popup Dialog matching BlankLayout.js & Live Website */}
        <Dialog
          fullWidth
          maxWidth="sm"
          open={openDialog}
          onClose={handleClose}
        >
          <DialogTitle className="popup-section-title">
            <div className="contact-us-form my-0">
              <h1>Enquire Now</h1>
            </div>
          </DialogTitle>
          <IconButton
            className="popup-section-icon"
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent className="popup-section">
            <div className="contact-us-form-mobile">
              <form onSubmit={handleSubmit}>
                <div className="contact-flex">
                  <input
                    className="floating-label"
                    placeholder="Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="contact-flex">
                  <input
                    placeholder="Phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="contact-flex">
                  <input
                    placeholder="Company Name"
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="contact-flex">
                  <select
                    name="business"
                    className="contact-select-div"
                    value={formData.business}
                    onChange={handleChange}
                    required
                  >
                    <option value="none">Type Of Business</option>
                    <option value="B2B">B2B</option>
                    <option value="B2C">B2C</option>
                  </select>
                </div>
                <div className="contact-flex">
                  <input
                    placeholder="City"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="contact-flex">
                  <input
                    placeholder="Email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="contact-flex">
                  <textarea
                    placeholder="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="chbox-div">
                  <input
                    type="checkbox"
                    className="chbox"
                    name="policyConsent"
                    checked={formData.policyConsent}
                    onChange={handleChange}
                    required
                  />
                  I hereby authorize to send notifications on SMS/Messages/Promotional/Informational Messages
                </div>
                <h4>
                  <button type="submit" disabled={loading}>
                    {loading ? "Submitting..." : "Submit Now"}
                  </button>
                </h4>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </SeoEnquiryContext.Provider>
  );
};

export default SeoLayout;
